import type { ClassViewModel } from 'src/api/types/ClassViewModel';

import { Tooltip, TableCell } from '@mui/material';

import { MESSAGES } from 'src/constants/messages';
import { StyledTooltipLabel } from 'src/sections/employer-data/shared/styles/sharedStyles';

// Just the relevant properties from the ClassViewModel.
// Grabbing them through the type system rather than
// copy-pasting so that it's automatically kept in sync.
type ClassCodeCellInput = Omit<
  ClassViewModel,
  Exclude<keyof ClassViewModel, 'classCode' | 'description' | 'classEnum'>
>;
export type ClassCodeCellProps = ClassCodeCellInput & {
  /**
   * Prefix appended to the data-testId properties of the child elements ('-' should not included)
   */
  dataTestIdPrefix?: string;

  /**
   * Fallback value to display when class code is not available.
   * Default behavior is to display an empty string.
   */
  fallbackValue?: string;
};

/**
 * Usage:
 *
 *      `<ClassCodeTableCell classCode={data.classCode?.classCode} description={data.classCode?.description} />`
 */
export function ClassCodeTableCell(props: ClassCodeCellProps) {
  const classCode = props.classCode ?? undefined;
  const isInvalid = props.classEnum === 'Invalid';
  const classDescription = isInvalid ? MESSAGES.CLASS_CODE.INVALID_TOOLTIP : (props.description ?? undefined);
  const fallbackValue = props.fallbackValue;
  const dataTestIdPrefix = props.dataTestIdPrefix ?? 'class-code';
  return (
    <TableCell data-testid={`${dataTestIdPrefix}-cell`}>
      <Tooltip arrow title={classDescription} placement="top">
        <StyledTooltipLabel
          data-testid={`${dataTestIdPrefix}-tooltip-label`}
          color="primary"
          sx={{
            display: 'inline-block',
            textDecorationLine: 'underline',
            textDecorationStyle: 'dotted',
          }}
        >
          {classCode ?? fallbackValue ?? ''}
        </StyledTooltipLabel>
      </Tooltip>
    </TableCell>
  );
}
